import { Form } from 'react-router'
import { ProviderIcon } from '#app/components/site/auth/ProviderIcon'
import { StatusButton } from '#app/components/ui/status-button'
import { useIsPending } from './misc'
import {
	APPLE_PROVIDER_NAME,
	FACEBOOK_PROVIDER_NAME,
	GOOGLE_PROVIDER_NAME,
	type ProviderName,
} from './provider-names'

export {
	APPLE_PROVIDER_NAME,
	FACEBOOK_PROVIDER_NAME,
	GOOGLE_PROVIDER_NAME,
	ProviderNameSchema,
	type ProviderName,
	providerNames,
} from './provider-names'

export const providerLabels: Record<ProviderName, string> = {
	[GOOGLE_PROVIDER_NAME]: 'Google',
	[APPLE_PROVIDER_NAME]: 'Apple',
	[FACEBOOK_PROVIDER_NAME]: 'Facebook',
} as const

export const providerIcons: Record<ProviderName, React.ReactNode> = {
	[GOOGLE_PROVIDER_NAME]: (
		<ProviderIcon provider="google" className="h-4 w-4" />
	),
	[APPLE_PROVIDER_NAME]: <ProviderIcon provider="apple" className="h-4 w-4" />,
	[FACEBOOK_PROVIDER_NAME]: (
		<ProviderIcon provider="facebook" className="h-4 w-4" />
	),
} as const

/**
 * The providers this environment offers (`ENV.SOCIAL_LOGIN_PROVIDERS`, from
 * `isSocialProviderEnabled`: the provider's *_LOGIN_ENABLED switch, plus its
 * keys). Google stays on when its switch is unset.
 */
export function enabledProviderNames(): ProviderName[] {
	// Empty is valid (every switch off). Without ENV at all (unit tests),
	// fall back to Google, the historic default.
	const enabled =
		typeof ENV !== 'undefined' ? ENV.SOCIAL_LOGIN_PROVIDERS : undefined
	return enabled ? [...enabled] : [GOOGLE_PROVIDER_NAME]
}

export function ProviderConnectionForm({
	redirectTo,
	type,
	providerName,
}: {
	redirectTo?: string | null
	type: 'Connect' | 'Login' | 'Continue'
	providerName: ProviderName
}) {
	const label = providerLabels[providerName]
	const formAction = `/auth/${providerName}`
	const isPending = useIsPending({ formAction })
	return (
		<Form
			className="flex items-center justify-center gap-2"
			action={formAction}
			method="POST"
		>
			{redirectTo ? (
				<input type="hidden" name="redirectTo" value={redirectTo} />
			) : null}
			<StatusButton
				type="submit"
				className="w-full border py-[16px] h-[55px] border-[#CCCCCC] rounded-[30px] cursor-pointer"
				status={isPending ? 'pending' : 'idle'}
				variant="ghost"
			>
				<span className="inline-flex items-center gap-1.5 text-[#32302F] text-[18px]">
					{providerIcons[providerName]}
					<span>
						{type} with {label}
					</span>
				</span>
			</StatusButton>
		</Form>
	)
}
